import { motion } from "framer-motion";
import { CheckCircle2, FileCheck2, FlaskConical } from "lucide-react";
import SectionHead from "../SectionHead";

const IMG =
    "https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=1400&auto=format&fit=crop";

const STAGES = [
    {
        tag: "Incoming",
        items: [
            "Raw material identification",
            "Visual inspection",
            "Dimensional verification",
            "Material documentation review",
        ],
    },
    {
        tag: "In-Process",
        items: [
            "Dimensions",
            "Thickness",
            "Hole positions",
            "Component geometry",
            "Surface condition",
            "Moulding quality",
        ],
    },
    {
        tag: "Final",
        items: [
            "Overall dimensions",
            "Thickness",
            "Hole/slot dimensions",
            "Visual appearance",
            "Surface finish",
            "Quantity verification",
            "Packing inspection",
        ],
    },
];

const DOCS = [
    "Material Test Certificate",
    "Inspection Report",
    "Dimensional Inspection Report",
    "Test Certificate",
    "Certificate of Conformance",
];

export const Quality = () => (
    <section id="quality" className="py-24 md:py-32 scroll-mt-20 bg-panel/40 border-y border-zinc-900">
        <div className="shell">
            <SectionHead
                num="06"
                overline="Quality Control"
                title="Inspected at every stage"
                desc="Quality inspection is carried out at different stages of the manufacturing process."
            />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                <div className="lg:col-span-7 grid md:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
                    {STAGES.map((stage, i) => (
                        <motion.div
                            key={stage.tag}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            data-testid={`quality-stage-${stage.tag.toLowerCase()}`}
                            className="bg-[#0e0e11] hover:bg-panel transition-colors duration-300 p-6"
                        >
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-safety mb-5">
                                {stage.tag} Inspection
                            </div>
                            <ul className="space-y-3">
                                {stage.items.map((item) => (
                                    <li key={item} className="flex items-start gap-2.5 text-sm text-zinc-300 leading-snug">
                                        <CheckCircle2 size={14} className="text-safety mt-0.5 shrink-0" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    ))}
                </div>

                <div className="lg:col-span-5 space-y-6">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                        className="relative border border-zinc-800 group overflow-hidden h-[240px]"
                    >
                        <img
                            src={IMG}
                            alt="Dimensional inspection"
                            className="absolute inset-0 w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 transition-all duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-transparent" />
                        <div className="absolute bottom-0 inset-x-0 p-5 font-mono text-[10px] uppercase tracking-[0.25em] text-safety">
                            Dimensional Verification
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.6, delay: 0.25 }}
                        className="border border-zinc-800 bg-[#0e0e11] p-6"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <FlaskConical size={18} className="text-safety" />
                            <span className="font-display uppercase font-semibold tracking-wide text-lg text-zinc-50">
                                Testing & Documentation
                            </span>
                        </div>
                        <p className="text-zinc-400 text-sm leading-relaxed mb-5">
                            Testing can be arranged/performed according to the
                            customer's specification and applicable
                            material/product requirements.
                        </p>
                        <ul className="space-y-2.5">
                            {DOCS.map((doc) => (
                                <li
                                    key={doc}
                                    className="flex items-center gap-2.5 text-sm text-zinc-300"
                                >
                                    <FileCheck2 size={14} className="text-safety shrink-0" />
                                    {doc}
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                </div>
            </div>
        </div>
    </section>
);

export default Quality;